import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Megaphone, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { useUserRole } from "@/hooks/useUserRole";
import { Button } from "@/components/ui/button";
import {
  type AdminAnnouncement,
  type AccountTier,
  type AnnouncementInput,
  type AnnouncementSeverity,
  createAnnouncement,
  deleteAnnouncement,
  getAnnouncementStatus,
  listAnnouncementsAdmin,
  listReadsForAnnouncement,
  markAnnouncementRead,
  publishAnnouncement,
  resolveVideoEmbed,
  unpublishAnnouncement,
  updateAnnouncement,
} from "@/lib/announcements";
import { AppSeo } from "@/components/AppSeo";

import {
  type FormState,
  emptyForm,
  fromAnnouncement,
  fromLocalInput,
} from "@/components/admin/announcements/announcementHelpers";
import { AnnouncementPreviewDialog } from "@/components/admin/announcements/AnnouncementPreviewDialog";
import { AnnouncementsListCard } from "@/components/admin/announcements/AnnouncementsListCard";
import { AnnouncementEditorCard } from "@/components/admin/announcements/AnnouncementEditorCard";

const AdminAnnouncements = () => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const navigate = useNavigate();

  const [items, setItems] = useState<AdminAnnouncement[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [readsCounts, setReadsCounts] = useState<Record<string, number>>({});
  const [severityFilter, setSeverityFilter] = useState<AnnouncementSeverity | "all">("all");
  const [audienceFilter, setAudienceFilter] = useState<AccountTier | "all" | "all_users">("all");
  const [statusFilter, setStatusFilter] = useState<"all" | "draft" | "scheduled" | "live" | "expired">("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"newest" | "oldest" | "most_read">("newest");
  const [bulkMarking, setBulkMarking] = useState(false);
  const [hideExpired, setHideExpired] = useState(true);
  const [previewing, setPreviewing] = useState<AdminAnnouncement | null>(null);

  const expiredCount = useMemo(
    () => items.filter((a) => getAnnouncementStatus(a) === "expired").length,
    [items],
  );

  const filteredItems = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const filtered = items.filter((a) => {
      const status = getAnnouncementStatus(a);
      if (severityFilter !== "all" && a.severity !== severityFilter) return false;
      if (statusFilter !== "all" && status !== statusFilter) return false;
      // Default: hide expired unless explicitly viewing them via the toggle
      // or via the Status=Expired filter.
      if (hideExpired && statusFilter !== "expired" && status === "expired") return false;
      if (q && !a.title.toLowerCase().includes(q) && !a.body_md.toLowerCase().includes(q)) return false;
      if (audienceFilter === "all") return true;
      if (audienceFilter === "all_users") return a.audience === "all";
      return a.audience === "tier" && a.audience_tier === audienceFilter;
    });
    const sorted = [...filtered].sort((a, b) => {
      if (sortBy === "most_read") return (readsCounts[b.id] ?? 0) - (readsCounts[a.id] ?? 0);
      const da = new Date(a.created_at).getTime();
      const db = new Date(b.created_at).getTime();
      return sortBy === "oldest" ? da - db : db - da;
    });
    // Pinned floats to the top within the chosen sort
    return sorted.sort((a, b) => Number(b.is_pinned) - Number(a.is_pinned));
  }, [items, severityFilter, audienceFilter, statusFilter, searchQuery, sortBy, readsCounts, hideExpired]);

  const refresh = async () => {
    try {
      setLoading(true);
      const data = await listAnnouncementsAdmin();
      setItems(data);
      const counts: Record<string, number> = {};
      await Promise.all(
        data.map(async (a) => {
          try {
            const r = await listReadsForAnnouncement(a.id);
            counts[a.id] = r.length;
          } catch {
            counts[a.id] = 0;
          }
        }),
      );
      setReadsCounts(counts);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) void refresh();
  }, [isAdmin]);

  const previewVideo = useMemo(() => (form.video_url ? resolveVideoEmbed(form.video_url) : null), [form.video_url]);

  const startNew = () => {
    setEditingId(null);
    setForm(emptyForm);
  };

  const startEdit = (a: AdminAnnouncement) => {
    setEditingId(a.id);
    setForm(fromAnnouncement(a));
  };

  const buildInput = (): AnnouncementInput => ({
    title: form.title,
    body_md: form.body_md,
    video_url: form.video_url || null,
    severity: form.severity,
    audience: form.audience,
    audience_tier: form.audience === "tier" ? form.audience_tier : null,
    is_pinned: form.is_pinned,
    published_at: editingId
      ? form.publish_now
        ? new Date().toISOString()
        : fromLocalInput(form.scheduled_at)
      : form.publish_now
        ? new Date().toISOString()
        : fromLocalInput(form.scheduled_at),
    expires_at: fromLocalInput(form.expires_at),
  });

  const handleSave = async (publish: boolean) => {
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSaving(true);
    try {
      const input = buildInput();
      if (publish && !input.published_at) input.published_at = new Date().toISOString();
      if (editingId) {
        await updateAnnouncement(editingId, input);
        toast.success("Announcement updated");
      } else {
        const created = await createAnnouncement(input, user.id);
        toast.success("Announcement created");
        setEditingId(created.id);
      }
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteAnnouncement(id);
      toast.success("Deleted");
      if (editingId === id) startNew();
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleTogglePublish = async (a: AdminAnnouncement) => {
    try {
      if (a.published_at) {
        await unpublishAnnouncement(a.id);
        toast.success("Unpublished");
      } else {
        await publishAnnouncement(a.id);
        toast.success("Published");
      }
      await refresh();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  const handleBulkMarkRead = async () => {
    if (!user || filteredItems.length === 0) return;
    setBulkMarking(true);
    let ok = 0;
    let failed = 0;
    await Promise.all(
      filteredItems.map(async (a) => {
        try {
          await markAnnouncementRead(a.id, user.id);
          ok += 1;
        } catch {
          failed += 1;
        }
      }),
    );
    setBulkMarking(false);
    if (failed === 0) toast.success(`Marked ${ok} as read`);
    else toast.warning(`Marked ${ok} as read, ${failed} failed`);
    await refresh();
  };

  const handleDuplicate = async (a: AdminAnnouncement, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!user) return;
    try {
      const created = await createAnnouncement(
        {
          title: `${a.title} (copy)`,
          body_md: a.body_md,
          video_url: a.video_url,
          severity: a.severity,
          audience: a.audience,
          audience_tier: a.audience_tier,
          is_pinned: false,
          published_at: null,
          expires_at: null,
        },
        user.id,
      );
      toast.success("Duplicated as draft");
      setEditingId(created.id);
      setForm(fromAnnouncement(created));
      await refresh();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  return (
    <>
    <AppSeo title="Announcements" />
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/admin")}>
            <ArrowLeft className="h-4 w-4" /> Admin
          </Button>
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2">
              <Megaphone className="h-6 w-6 text-primary" /> Announcements
            </h1>
            <p className="text-sm text-muted-foreground">
              Send in-app notifications to logged-in Excerptus users. Text and optional video.
            </p>
          </div>
        </div>
        <Button onClick={startNew} variant={editingId ? "outline" : "default"}>
          <Plus className="h-4 w-4" /> New
        </Button>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_minmax(0,1.2fr)]">
        <AnnouncementsListCard
          loading={loading}
          items={items}
          filteredItems={filteredItems}
          readsCounts={readsCounts}
          editingId={editingId}
          searchQuery={searchQuery}
          setSearchQuery={setSearchQuery}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          severityFilter={severityFilter}
          setSeverityFilter={setSeverityFilter}
          audienceFilter={audienceFilter}
          setAudienceFilter={setAudienceFilter}
          sortBy={sortBy}
          setSortBy={setSortBy}
          hideExpired={hideExpired}
          setHideExpired={setHideExpired}
          expiredCount={expiredCount}
          bulkMarking={bulkMarking}
          onBulkMarkRead={handleBulkMarkRead}
          onStartEdit={startEdit}
          onPreview={setPreviewing}
          onDuplicate={handleDuplicate}
        />

        <AnnouncementEditorCard
          editingId={editingId}
          form={form}
          setForm={setForm}
          saving={saving}
          previewVideo={previewVideo}
          items={items}
          userId={user.id}
          onSave={handleSave}
          onTogglePublish={(a) => void handleTogglePublish(a)}
          onDelete={(id) => void handleDelete(id)}
          onPreview={setPreviewing}
          onStartNew={startNew}
        />
      </div>

      <AnnouncementPreviewDialog previewing={previewing} onClose={() => setPreviewing(null)} />
    </div>
    </>
  );
};

export default AdminAnnouncements;
